<script setup>
// Vue Imports
import { ref, watch, computed, provide } from 'vue';
import { useRouter } from "vue-router/dist/vue-router";;
import { useQuery, useMutation, provideApolloClient } from '@vue/apollo-composable'
import { useWindowSize } from '@vueuse/core';
import { XMarkIcon, Cog6ToothIcon, LightBulbIcon } from "@heroicons/vue/24/outline";

// Component Imports
import Header from '@/components/layout/Header.vue'
import MWIFooter from '@/components/mwi/Footer.vue'
import MWPFooter from '@/components/mwp/Footer.vue'
import SubNav from '@/components/dashboard/SubNav.vue'
import LoadingOverlay from "@/components/ui/LoadingOverlay.vue"
import CustomerStatus from "@/components/molecules/CustomerStatus.vue";

// Others
import { apolloClient } from "@/utils/apolloClient";
provideApolloClient(apolloClient);
import { PORTFOLIO_ACCESS_UNAVAILABLE, DOCUMENT_ACCESS_UNAVAILABLE } from "@/utils/systemMessages";
import { APP } from "@/config/app";
import { useDateOfBirthVerificationStatusStore } from '@/stores/dateOfBirthVerificationStatus';
import { useKillSwitchFlagsStore } from '@/stores/storeKillSwitchFlags';
import { useStoreDismissedMessages } from "@/stores/storeDismissedMessages";
import { gqlGetPortfolios } from "@/graphql/portfolios";
import { gqlGetClientInfo, gqlGetClientServices, gqlMarkAnnouncementRead } from "@/graphql/clients";

// Component Logic
const router = useRouter();

const storeDateOfBirthVerificationStatus = useDateOfBirthVerificationStatusStore();
const storeKillSwitchFlags = useKillSwitchFlagsStore();
const messageDismissal = useStoreDismissedMessages();

const { result: portfoliosResult, loading: portfoliosLoading, error: portfoliosError } = useQuery(gqlGetPortfolios, { page: 1, pageSize: 10 },  () => ({
  fetchPolicy: 'no-cache',
}));

const hasSelfSelectPortfolio = computed(function() {
  let selfSelect = portfoliosResult?.value?.clientInfo?.portfolios?.elements?.filter(portfolio => portfolio.service === 'SELF_SELECT' && (portfolio.type === 'ISA' || portfolio.type === 'GENERAL_INVESTMENT_ACCOUNT'))
  return selfSelect?.length > 0
});

const { result: getClientServicesResult, loading: getClientServicesLoading, error: getClientServicesError, onResult: clientServicesOnResult  } = useQuery(gqlGetClientServices, {}, () => ({
  fetchPolicy: 'no-cache'
}));

const forceRerender = ref(0);
const corporateClientServices = computed(function() {
  return getClientServicesResult?.value?.clientInfo;
});

clientServicesOnResult((result) => {
  forceRerender.value += 1;
});

let { width } = useWindowSize();

const { result: resultClient, loading: loadingClient, error: errorClient  } = useQuery(gqlGetClientInfo, {},  () => ({
  fetchPolicy: 'no-cache',
}));

watch(resultClient,value => {
  // does this client need to verify their date of birth before accessing the Dashboard?
  if(storeDateOfBirthVerificationStatus.isVerificationRequired && !storeDateOfBirthVerificationStatus.isVerified && resultClient.value.clientInfo?.birthdateVerificationStatus !== 'NOT_NEEDED'){
    router.replace({
      name: "VerifyDob"
    });
  } else {
    storeDateOfBirthVerificationStatus.verificationRequired = false;
  }

  storeKillSwitchFlags.appDisabled = resultClient.value.clientInfo.features.appDisabled;
  storeKillSwitchFlags.dashboardDisabled = resultClient.value.clientInfo.features.dashboardDisabled;
  storeKillSwitchFlags.documentsDisabled = resultClient.value.clientInfo.features.documentsDisabled;

  forceRerender.value += 1;
});

const dismissSpecificMessage = () => {
  if(resultClient && resultClient.value.clientInfo)
  {
    messageDismissal.saveDismissedMessage(resultClient?.value?.clientInfo?.announcement?.id)
  }
}

const messageNotSet  = computed(() => {
  return resultClient?.value?.clientInfo?.announcement?.id !== messageDismissal.getMessageId()
})

provide('layoutLoading', loadingClient);
</script>

<template>
  <Header />

  <div class="relative flex-1 flex flex-col">
    <SubNav
      v-if="['INVESTED', 'FUNDED', 'WRAPPER_CREATED', 'WITHDRAWN'].includes(resultClient?.clientInfo.status) || resultClient?.clientInfo.type === 'ADVISED'"
      v-show="width > 768"
      :type="resultClient?.clientInfo.type"
      :clientInfo="resultClient?.clientInfo"
      :killSwitchFlags="storeKillSwitchFlags"
      :hasSelfSelectPortfolio = "hasSelfSelectPortfolio"
      :corporateClientServices = "corporateClientServices"
      :key="forceRerender"
    />
    <LoadingOverlay v-if="loadingClient">Retrieving your details...</LoadingOverlay>
    <div class="bg-sandstone/90">
      <div
        v-if="resultClient?.clientInfo && resultClient.clientInfo.announcement && resultClient.clientInfo.announcement.message.length > 0 && messageNotSet && !loadingClient"
        class="mx-auto relative rounded-2xl my-20 bg-warning-light text-warning-dark max-w-7xl flex flex-col justify-items-center px-8 py-8 min-h-[4rem] flex-1"
      >
        <XMarkIcon
          @click="dismissSpecificMessage"
          class="hover:text-red-500 cursor-pointer absolute right-5 top-5 h-5 w-5"
        />
        <h2 class="mb-4" v-if="resultClient?.clientInfo.announcement.title">
          <Cog6ToothIcon class="h-8 w-8 pb-1 font-bolder inline" /> {{ resultClient?.clientInfo.announcement.title }}
        </h2>
        <pre
          style="text-wrap: wrap !important;"
          class="body-3-light ml-9"
          v-html="resultClient?.clientInfo.announcement.message"></pre>
      </div>

      <div
        v-else-if="!loadingClient && resultClient?.clientInfo && resultClient?.clientInfo?.features?.appDisabled || resultClient?.clientInfo?.features?.dashboardDisabled || resultClient?.clientInfo?.features?.documentsDisabled"
        class="mx-auto my-20 relative rounded-2xl bg-danger-light text-danger-dark max-w-7xl flex flex-col justify-items-center p-8 min-h-[4rem] flex-1"
      >
        <h2 class="mb-4">
          <LightBulbIcon class="h-8 w-8 pb-1 font-bolder inline" />
          <span v-if="resultClient?.clientInfo?.features?.appDisabled || resultClient?.clientInfo?.features?.dashboardDisabled">{{ PORTFOLIO_ACCESS_UNAVAILABLE.title }}</span>
          <span v-else-if="resultClient?.clientInfo?.features?.documentsDisabled">{{ DOCUMENT_ACCESS_UNAVAILABLE.title }}</span>
        </h2>
        <p
          v-if="resultClient?.clientInfo?.features?.appDisabled || resultClient?.clientInfo?.features?.dashboardDisabled"
          class="body-3-light ml-9"
          v-html="PORTFOLIO_ACCESS_UNAVAILABLE.message"
        ></p>
        <p
          v-else-if="resultClient?.clientInfo?.features?.documentsDisabled"
          class="body-3-light ml-9"
          v-html="DOCUMENT_ACCESS_UNAVAILABLE.message"
        ></p>
      </div>


      <div class="min-h-[52rem] flex-1 bg-sandstone/60">
        <CustomerStatus
          v-if="!loadingClient"
          :name="resultClient?.clientInfo.firstName"
          :status="
            resultClient?.clientInfo.externalReference !== 'unknown'
              ? resultClient?.clientInfo.externalReference
              : 'Pending'
          "
        />

        <div>
          <slot />
        </div>
      </div>
    </div>
  </div>

  <MWPFooter v-if="APP.MyWealthMode" />
  <MWIFooter v-else />
</template>
